import axiosInstance from '../axiosInstance';

/**
 * Un champ texte laissé vide vaut la chaîne vide ; un champ numérique ou une
 * date valent null. Les confondre fait rejeter tout le formulaire, parce que
 * les colonnes texte du modèle acceptent le vide mais refusent le nul.
 */
export function nettoyer(champs, numeriques = []) {
  return Object.fromEntries(
    Object.entries(champs).map(([nom, valeur]) => {
      if (valeur !== '' && valeur !== undefined) return [nom, valeur];
      return [nom, numeriques.includes(nom) ? null : ''];
    }),
  );
}

/**
 * Appels du parcours assuré.
 *
 * Regroupés ici pour que les écrans ne connaissent que des intentions
 * — « enregistrer la déclaration » — et jamais des URL.
 */

export async function creerDossier({ name, reference }) {
  const { data } = await axiosInstance.post('/expertise/', {
    name: name || 'Mon sinistre',
    reference: reference || '',
  });
  return data;
}

export async function lireDossier(expertiseId) {
  const { data } = await axiosInstance.get(`/expertise/${expertiseId}/`);
  return data;
}

/** Le nom et la référence interne du dossier. Ni l'un ni l'autre obligatoire. */
export async function majDossier(expertiseId, champs) {
  const { data } = await axiosInstance.patch(`/expertise/${expertiseId}/`,
                                             champs);
  return data;
}

export async function listerMesDossiers() {
  const { data } = await axiosInstance.get('/expertise/');
  return data.results ?? data;
}

/**
 * Un objet du contenu, prêt pour l'API.
 *
 * Les montants sont `blank` mais pas `null` en base : un champ laissé vide
 * vaut zéro, pas nul. La date, elle, accepte le nul — c'est la seule façon de
 * dire « je ne sais plus quand je l'ai acheté ».
 */
function objetPourApi(champs) {
  const montants = ['amount_buy', 'amount_ask', 'amount_propose'];
  return Object.fromEntries(
    Object.entries(champs)
      .filter(([nom]) => nom !== 'id')
      .map(([nom, valeur]) => {
        if (montants.includes(nom)) return [nom, valeur === '' || valeur === undefined ? 0 : valeur];
        if (nom === 'date_buy') return [nom, valeur || null];
        return [nom, valeur ?? ''];
      }),
  );
}

/** L'inventaire du contenu : mobilier, électroménager, effets personnels. */
export async function listerContenu(expertiseId) {
  const { data } = await axiosInstance.get('/contents/', {
    params: { expertise_id: expertiseId },
  });
  return data.results ?? data;
}

export async function totalContenu(expertiseId) {
  const { data } = await axiosInstance.get(`/totalcontent/${expertiseId}/`);
  return data;
}

export async function creerObjet(expertiseId, champs) {
  const { data } = await axiosInstance.post(
    `/contents/?expertise_id=${expertiseId}`, objetPourApi(champs),
  );
  return data;
}

export async function modifierObjet(id, champs) {
  const { data } = await axiosInstance.patch(`/contents/${id}/`,
                                             objetPourApi(champs));
  return data;
}

export async function supprimerObjet(id) {
  await axiosInstance.delete(`/contents/${id}/`);
}

/**
 * Le questionnaire d'une nature de sinistre, ou la liste des natures.
 *
 * Il vient de la base : l'expert l'affine dans l'admin sans passer par le
 * code. « Je ne sais pas encore » est ajouté par le serveur à chaque
 * question — le front n'a pas à y penser.
 */
export async function lireQuestionnaire(nature) {
  const { data } = await axiosInstance.get('/declaration/questionnaire/',
                                           { params: { nature } });
  return data;
}

/**
 * Relit le récit : ce qui manque, ce qui se contredit, une reformulation.
 *
 * Un appel payant, déclenché par l'assuré et par personne d'autre. Rien n'est
 * enregistré : la reformulation est proposée, jamais imposée.
 */
export async function relireDeclaration(expertiseId) {
  const { data } = await axiosInstance.post(
    `/expertises/${expertiseId}/relire-declaration/`);
  return data;
}

/** Les postes du chiffrage : bâtiment, mais aussi relogement, honoraires… */
export async function creerPoste(expertiseId, totalCostingId, champs) {
  const { data } = await axiosInstance.post('/postcostings/', {
    ...champs, totalCosting: totalCostingId,
  });
  return data;
}

export async function modifierPoste(id, champs) {
  const { data } = await axiosInstance.patch(`/postcostings/${id}/`, champs);
  return data;
}

export async function supprimerPoste(id) {
  await axiosInstance.delete(`/postcostings/${id}/`);
}

/** Le partage d'un dossier : qui y a accès, et qui est invité. */
export async function listerInvitations(expertiseId) {
  const { data } = await axiosInstance.get(
    `/expertises/${expertiseId}/invitations/`,
    { params: { origine: window.location.origin } });
  return data.results ?? data;
}

export async function inviter(expertiseId, { email, level, person_expertise }) {
  const { data } = await axiosInstance.post(
    `/expertises/${expertiseId}/invitations/`,
    { email, level, person_expertise: person_expertise || null,
      origine: window.location.origin },
  );
  return data;
}

export async function annulerInvitation(id) {
  await axiosInstance.delete(`/invitations/${id}/`);
}

export async function listerAcces(expertiseId) {
  const { data } = await axiosInstance.get(
    `/expertises/${expertiseId}/accesses/`);
  return data.results ?? data;
}

export async function revoquerAcces(expertiseId, id) {
  await axiosInstance.post(
    `/expertises/${expertiseId}/accesses/${id}/revoke/`);
}

export async function lireInvitation(token) {
  const { data } = await axiosInstance.get(`/invitations/${token}/accept/`);
  return data;
}

/** Relier son dossier à celui d'un intervenant : chacun garde le sien. */
export async function inviterARelier(expertiseId, intervenant, email) {
  const { data } = await axiosInstance.post(`/expertises/${expertiseId}/liaisons/`, {
    intervenant, email, origine: window.location.origin,
  });
  return data;
}

export async function accepterInvitation(token) {
  const { data } = await axiosInstance.post(`/invitations/${token}/accept/`);
  return data;
}

/** La réclamation assemblée, prête à être présentée à l'assureur. */
export async function lireReclamation(expertiseId, mode) {
  const { data } = await axiosInstance.get(
    `/expertises/${expertiseId}/reclamation/`, { params: { mode } });
  return data;
}

/** La réponse de l'assureur, mise en regard de la réclamation. */
export async function lireConfrontation(expertiseId) {
  const { data } = await axiosInstance.get(
    `/expertises/${expertiseId}/confrontation/`);
  return data;
}

export async function consignerReponse(expertiseId, champs) {
  const { data } = await axiosInstance.post(
    `/expertises/${expertiseId}/reponses-assureur/`, champs);
  return data;
}

export async function supprimerReponse(id) {
  await axiosInstance.delete(`/reponses-assureur/${id}/`);
}

/** Lit un courrier déposé. Propose, n'enregistre rien. */
export async function lireCourrierAssureur(fileId) {
  const { data } = await axiosInstance.post(
    `/fichiers/${fileId}/lire-reponse-assureur/`);
  return data;
}

export async function appliquerReponse(expertiseId, donnees) {
  const { data } = await axiosInstance.post(
    `/expertises/${expertiseId}/appliquer-reponse/`, donnees);
  return data;
}

export async function majPieceDemandee(id, champs) {
  const { data } = await axiosInstance.patch(`/pieces-demandees/${id}/`, champs);
  return data;
}

/** Qui je suis, pour qui je travaille, et ce que je peux faire. */
/** Annule un dossier : il disparaît des listes, rien n'est effacé. */
export async function annulerDossier(expertiseId) {
  const { data } = await axiosInstance.post(
    `/expertises/${expertiseId}/annuler/`);
  return data;
}

/** Une question, un rappel, ou une demande d'accompagnement. */
export async function demanderSupport(champs) {
  const { data } = await axiosInstance.post('/support/', champs);
  return data;
}

export async function listerMesDemandes() {
  const { data } = await axiosInstance.get('/support/');
  return data;
}

/** Ce que le contrat prévoit pour un expert d'assuré. Jamais un prix. */
export async function lireAssistance(expertiseId) {
  const { data } = await axiosInstance.get(
    `/expertises/${expertiseId}/assistance/`);
  return data;
}

export async function lireProfil() {
  const { data } = await axiosInstance.get('/users/me/profil/');
  return data;
}

/** Corrige ses propres coordonnées. Ni son rôle, ni son organisation. */
export async function majProfil(champs) {
  const { data } = await axiosInstance.patch('/users/me/profil/', champs);
  return data;
}

export async function lireContrat(expertiseId) {
  const { data } = await axiosInstance.get(`/contract/${expertiseId}/`);
  return data;
}

export async function majContrat(expertiseId, champs) {
  const { data } = await axiosInstance.patch(`/contract/${expertiseId}/`,
                                             nettoyer(champs, []));
  return data;
}

/**
 * Ouvre un dépôt mobile et rend le QR à afficher.
 *
 * Photographier une police depuis un PC est impossible : le QR fait passer la
 * main au téléphone, pour un temps court.
 */
export async function ouvrirDepotMobile(expertiseId, { minutes, categorie,
                                                       dommage, contenu } = {}) {
  const { data } = await axiosInstance.post(
    `/expertises/${expertiseId}/depot-mobile/`,
    // Le serveur ne sait pas par quelle adresse ce navigateur l'a joint : le
    // front tourne sur un autre port. On la lui donne, c'est la seule qu'un
    // téléphone du même réseau puisse atteindre.
    { minutes, categorie, dommage, contenu,
      origine: window.location.origin },
  );
  return data;
}

export async function suivreDepotMobile(jeton) {
  const { data } = await axiosInstance.get(`/depot-mobile/${jeton}/`);
  return data;
}

export async function fermerDepotMobile(jeton) {
  await axiosInstance.delete(`/depot-mobile/${jeton}/`);
}

/** Pièce du dossier : conditions particulières, avenant, police scannée. */
export async function televerserPiece(expertiseId, fichier, categorie, titre) {
  const charge = new FormData();
  charge.append('expertise', expertiseId);
  charge.append('fichier', fichier);
  charge.append('title', titre || fichier.name);
  charge.append('category', categorie);
  const { data } = await axiosInstance.post('/files/', charge, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/** Une pièce rattachée à un objet du contenu : photo, facture, devis. */
export async function televerserPieceContenu(expertiseId, contenuId, fichier,
                                             categorie) {
  const charge = new FormData();
  charge.append('expertise', expertiseId);
  charge.append('contenu', contenuId);
  charge.append('fichier', fichier);
  charge.append('title', fichier.name);
  charge.append('category', categorie || 'photo');
  const { data } = await axiosInstance.post('/files/', charge, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function piecesDuContenu(expertiseId, contenuId) {
  const { data } = await axiosInstance.get('/files/', {
    params: { expertise_id: expertiseId },
  });
  return (data.results ?? data).filter((f) => f.contenu === contenuId);
}

/** Toutes les pièces du dossier, pour décider lesquelles partent. */
export async function listerToutesLesPieces(expertiseId) {
  const { data } = await axiosInstance.get('/files/', {
    params: { expertise_id: expertiseId },
  });
  return data.results ?? data;
}

export async function joindreOuNon(fileId, joindre) {
  const charge = new FormData();
  charge.append('joindre', joindre);
  const { data } = await axiosInstance.put(`/files/${fileId}/`, charge, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function listerPieces(expertiseId, categorie) {
  const { data } = await axiosInstance.get('/files/', {
    params: { expertise_id: expertiseId, category: categorie },
  });
  const liste = data.results ?? data;
  return categorie ? liste.filter((f) => f.category === categorie) : liste;
}

export async function listerCompagnies() {
  const { data } = await axiosInstance.get('/referential/compagnies/');
  return data.results ?? data;
}

/** Conditions générales publiées, filtrables par compagnie. */
export async function listerConditions(compagnieId) {
  const { data } = await axiosInstance.get('/referential/general-conditions/');
  const liste = data.results ?? data;
  return compagnieId
    ? liste.filter((c) => (c.compagnie ?? c.insurance) === compagnieId)
    : liste;
}

/**
 * Cherche un intermédiaire au registre du Commissariat aux Assurances.
 *
 * Le registre donne la qualité réelle — courtier ou agent — et le matricule.
 * L'assuré n'a pas à savoir laquelle : il tape un nom.
 */
export async function chercherIntermediaire(query) {
  if (!query || query.trim().length < 2) return [];
  const { data } = await axiosInstance.get('/referential/intermediaires/', {
    params: { query, type: 'CM,CP,AC,AG' },
  });
  return (data.results ?? data).slice(0, 20);
}

export async function listerIntervenants(expertiseId) {
  const { data } = await axiosInstance.get('/personne/', {
    params: { expertise_id: expertiseId },
  });
  return data.results ?? data;
}

export async function creerIntervenant(expertiseId, champs) {
  const { data } = await axiosInstance.post('/personne/', {
    ...nettoyer(champs, []), expertise: expertiseId,
  });
  return data;
}

export async function modifierIntervenant(id, champs) {
  const { data } = await axiosInstance.patch(`/personne/${id}/`,
                                             nettoyer(champs, []));
  return data;
}

export async function supprimerIntervenant(id) {
  await axiosInstance.delete(`/personne/${id}/`);
}

/** Le portefeuille : tous les dossiers atteignables, et où ils en sont. */
export async function lirePortefeuille() {
  const { data } = await axiosInstance.get('/portefeuille/');
  return data;
}

export async function lireSinistre(expertiseId) {
  const { data } = await axiosInstance.get(`/claims/${expertiseId}/`);
  return data;
}

export async function enregistrerSinistre(expertiseId, champs) {
  const { data } = await axiosInstance.patch(`/claims/${expertiseId}/`, champs);
  return data;
}

export async function listerGaranties() {
  const { data } = await axiosInstance.get('/guarantee/');
  return data.results ?? data;
}

export async function listerBatiments(expertiseId) {
  const { data } = await axiosInstance.get('/batiments/', {
    params: { expertise_id: expertiseId },
  });
  return data.results ?? data;
}

export async function enregistrerBatiment(expertiseId, champs, batimentId) {
  const charge = { ...champs, expertise: expertiseId };
  const { data } = batimentId
    ? await axiosInstance.patch(`/batiments/${batimentId}/`, charge)
    : await axiosInstance.post('/batiments/', charge);
  return data;
}

export async function listerDommages(expertiseId) {
  const { data } = await axiosInstance.get('/dommages/', {
    params: { expertise_id: expertiseId },
  });
  return data.results ?? data;
}

export async function enregistrerDommage(expertiseId, champs, dommageId) {
  const charge = { ...champs, expertise: expertiseId };
  const { data } = dommageId
    ? await axiosInstance.patch(`/dommages/${dommageId}/`, charge)
    : await axiosInstance.post('/dommages/', charge);
  return data;
}

export async function supprimerDommage(dommageId) {
  await axiosInstance.delete(`/dommages/${dommageId}/`);
}

export async function propositionsPourDommage(dommageId) {
  const { data } = await axiosInstance.get(`/dommages/${dommageId}/propositions/`);
  return data;
}

/**
 * Recherche dans le barème.
 *
 * Les propositions ne contiennent que ce qui correspond aux mots du dommage :
 * l'installation de chantier n'y sera jamais. Cette recherche-ci ouvre tout
 * le barème du pays du sinistre.
 */
export async function chercherBareme(query, pays) {
  if (!query || query.trim().length < 2) return [];
  const { data } = await axiosInstance.get('/pricinglist/', {
    params: { query, country: pays || undefined, page_size: 25 },
  });
  return data.results ?? data;
}

export async function retirerChiffrage(dommageId) {
  await axiosInstance.delete(`/dommages/${dommageId}/chiffrer/`);
}

export async function modelesPourDommage(dommageId, choix) {
  const params = choix && Object.keys(choix).length
    ? { choix: JSON.stringify(choix) }
    : undefined;
  const { data } = await axiosInstance.get(
    `/dommages/${dommageId}/modeles/`, { params });
  return data;
}

export async function appliquerModele(dommageId, modeleId, quantites, choix) {
  const { data } = await axiosInstance.post(
    `/dommages/${dommageId}/appliquer-modele/`,
    { modele: modeleId, quantites: quantites || {}, choix: choix || {} },
  );
  return data;
}

export async function analyserDommage(dommageId, avecPhotos = true) {
  const { data } = await axiosInstance.post(`/dommages/${dommageId}/analyser/`, {
    avec_photos: avecPhotos,
  });
  return data;
}

export async function chiffrerDommage(dommageId, lignes) {
  const { data } = await axiosInstance.post(`/dommages/${dommageId}/chiffrer/`, {
    lignes,
  });
  return data;
}

export async function lireChiffrage(expertiseId) {
  const { data } = await axiosInstance.get(`/totalcostings/${expertiseId}/`);
  return data;
}

export async function listerPostes(expertiseId) {
  const { data } = await axiosInstance.get('/postcostings/', {
    params: { expertise_id: expertiseId },
  });
  return data.results ?? data;
}

export async function syntheseDuDossier(expertiseId) {
  const { data } = await axiosInstance.get(
    `/expertises/${expertiseId}/synthese-revendication/`,
  );
  return data;
}

export async function televerserPhoto(expertiseId, dommageId, fichier, titre) {
  const charge = new FormData();
  charge.append('expertise', expertiseId);
  charge.append('dommage', dommageId);
  charge.append('fichier', fichier);
  charge.append('title', titre || fichier.name);
  charge.append('category', 'photo');
  const { data } = await axiosInstance.post('/files/', charge, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function supprimerPhoto(fileId) {
  await axiosInstance.delete(`/files/${fileId}/`);
}

// --- Suivi : tâches, envois aux dossiers reliés, pièces reçues -------------

export async function listerTaches(expertiseId) {
  const { data } = await axiosInstance.get('/tasks/', {
    params: { expertise_id: expertiseId },
  });
  return data.results ?? data;
}

export async function creerTache(expertiseId, champs) {
  const { data } = await axiosInstance.post('/tasks/', {
    ...champs, expertise: expertiseId,
  });
  return data;
}

export async function majTache(id, champs) {
  const { data } = await axiosInstance.patch(`/tasks/${id}/`, champs);
  return data;
}

export async function listerLiaisons(expertiseId) {
  const { data } = await axiosInstance.get(`/expertises/${expertiseId}/liaisons/`);
  return data.liaisons;
}

export async function envoyerAuDossierRelie(tacheId, liaison, pieceIds) {
  const { data } = await axiosInstance.post(`/tasks/${tacheId}/envoyer-dossier/`, {
    liaison, attachment_ids: pieceIds, origine: window.location.origin,
  });
  return data;
}

export async function traiterPiecesRecues(ids, action) {
  const { data } = await axiosInstance.post('/pieces-recues/', { ids, action });
  return data;
}

/**
 * Envoyer par courriel, depuis la plateforme : la déclaration ou la
 * réclamation produite à l'instant (`document`), et des pièces par lien.
 */
export async function envoyerDuDossier(expertiseId, { a, cc, objet, message,
                                                      pieces, document }) {
  const { data } = await axiosInstance.post(`/expertises/${expertiseId}/envois/`, {
    a, cc, objet, message, pieces, document: document || null,
    origine: window.location.origin,
  });
  return data;
}
